import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { CheckCircle, Home, Package, Clock, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Banner shown when the order was submitted after 10:00 AM Iraq local time —
// the warehouse only ships these the next morning starting from 8 AM.
// Dismissable so it doesn't block the receipt details below it.
const AfterCutoffNotice = ({ order }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  // Convert order timestamp (UTC ISO) or current time to Iraq local hour.
  const baseUtc = order?.created_at ? new Date(order.created_at) : new Date();
  // Iraq is UTC+3 year-round (no DST).
  const iraqHour = (baseUtc.getUTCHours() + 3) % 24;
  if (iraqHour < 10) return null; // Before 10 AM — same-day shipping window.

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        data-testid="after-cutoff-notice"
        className="relative bg-gradient-to-l from-[#FFF4E0] to-[#FEF3C7] border-2 border-[#F5A623] rounded-2xl p-4 mb-6 text-right shadow-md"
      >
        <button
          onClick={() => setDismissed(true)}
          data-testid="after-cutoff-dismiss"
          aria-label="إغلاق"
          className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white/80 hover:bg-white text-[#a06e10] flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F5A623] text-white flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-[#a06e10] text-base">⏰ فاتورتك ستجهز غداً</div>
            <p className="text-sm text-[#7c5510] mt-1 leading-relaxed">
              لأن طلبك وصل بعد الساعة <strong>10:00 صباحاً</strong>، سيتم تجهيزه غداً صباحاً
              ابتداءً من الساعة <strong>8:00</strong>. شكراً لتفهمك 💚
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Lightweight order-success confetti — fires once on mount for ~2.5s.
// Lighter than the login celebration so it doesn't block the receipt view.
const QuickConfetti = () => {
  const [pieces] = useState(() =>
    Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.3,
      duration: 2 + Math.random() * 1.5,
      size: 6 + Math.random() * 7,
      color: ["#7ED321", "#F5A623", "#3B82F6", "#E85D45", "#FBBF24", "#10B981"][i % 6],
      rotate: Math.random() * 360,
      shape: Math.random() > 0.5 ? "50%" : "2px",
    })),
  );
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-40">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          initial={{ y: -40, opacity: 1, rotate: p.rotate }}
          animate={{ y: "110vh", opacity: 0, rotate: p.rotate + 540 }}
          transition={{ delay: p.delay, duration: p.duration, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 1.4,
            background: p.color,
            borderRadius: p.shape,
            boxShadow: `0 0 4px ${p.color}80`,
          }}
        />
      ))}
    </div>
  );
};

const statusLabels = {
  pending: { label: "قيد المعالجة", color: "#F5A623" },
  confirmed: { label: "تم موافقة الطلب", color: "#4A90E2" },
  delivering: { label: "قيد التوصيل", color: "#E85D45" },
  delivered: { label: "تم التوصيل", color: "#7ED321" },
  rejected: { label: "مرفوض", color: "#999" },
};

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/orders/my`)
      .then((res) => {
        const found = res.data.find((o) => o.id === orderId);
        setOrder(found);
      })
      .catch(() => navigate("/"));
  }, [orderId, navigate]);

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-8" data-testid="order-success-page">
      <QuickConfetti />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-24 h-24 mx-auto mb-6 bg-[#7ED321]/10 rounded-full flex items-center justify-center"
        >
          <CheckCircle className="w-14 h-14 text-[#7ED321]" />
        </motion.div>

        <h1 className="font-heading font-bold text-3xl text-[#111814] mb-2">
          تم استلام طلبك!
        </h1>
        <p className="text-[#5C6E64] mb-4">
          سوف نتواصل معك قريباً لتأكيد الطلب
        </p>

        {/* 10 AM cutoff notice — orders placed after 10:00 AM Iraq time are
            deferred to the next day's preparation cycle (admin starts at
            8 AM). We compute the Iraq local hour client-side so the warning
            adapts to the customer's submission time without a server call. */}
        <AfterCutoffNotice order={order} />

        {order && (
          <div className="bg-[#FAFCFA] border border-[#E4EBE6] rounded-3xl p-6 mb-6 text-right">
            <div className="flex justify-between items-center mb-4 pb-4 border-b border-[#E4EBE6]">
              <span className="text-sm text-[#5C6E64]">رقم الطلب</span>
              <span data-testid="order-id" className="font-mono text-sm text-[#111814]">
                {order.id.slice(0, 8).toUpperCase()}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              {order.items.map((item) => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span className="text-[#5C6E64]">
                    {item.name_ar} × {item.quantity}
                  </span>
                  <span className="font-medium">{(item.price * item.quantity).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع</span>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-[#E4EBE6]">
              <span className="font-bold">المجموع</span>
              <span data-testid="order-total" className="font-bold text-xl text-[#E85D45]">
                {order.total.toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
              </span>
            </div>

            <div className="mt-4 inline-block px-3 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${statusLabels[order.status]?.color}20`,
                color: statusLabels[order.status]?.color
              }}
            >
              {statusLabels[order.status]?.label}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/"
            data-testid="back-home"
            className="flex items-center justify-center gap-2 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#2D4A3E] font-medium py-3 rounded-full transition-colors"
          >
            <Home className="w-4 h-4" />
            الرئيسية
          </Link>
          <Link
            to="/my-orders"
            data-testid="my-orders"
            className="flex items-center justify-center gap-2 bg-[#2D4A3E] hover:bg-[#233A30] text-white font-medium py-3 rounded-full transition-colors"
          >
            <Package className="w-4 h-4" />
            طلباتي
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderSuccess;
